import { isAxiosError } from 'axios';
import apiClient from './client';

const RETRY_DELAYS_MS = [1000, 2000];

function shouldRetryGetMe(err: unknown): boolean {
  if (isAxiosError(err) && err.response?.status != null) {
    const status = err.response.status;
    if (status >= 400 && status < 500) return false;
  }
  return true;
}

export type UserMeResponse = {
  telegramId: number;
  username: string | null;
  name: string | null;
  /** CHANGE: display name overrides (null = use Telegram) */
  firstNameOverride?: string | null;
  lastNameOverride?: string | null;
  middleNameOverride?: string | null;
  telegramFirstName?: string | null;
  telegramLastName?: string | null;
  photoUrl?: string | null;
  phone: string | null;
  email: string | null;
  /** Доступ к внутренней админ-панели /admin */
  isAdmin?: boolean;
  /** Когда пользователь нажал «Согласен» с политикой; null — не дал согласие */
  privacyPolicyAcceptedAt?: string | null;
  /** Версия политики на момент согласия (152-ФЗ) */
  privacyPolicyVersion?: string | null;
  welcomeBonusClaimed?: boolean;
  bonus: {
    balance: number;
    totalSpent: number;
    cashbackPercent: number;
    welcomeBonus: number;
    maxSpendPercent: number;
  };
};

export type ClaimWelcomeBonusResponse = {
  success: boolean;
  bonusBalance: number;
  welcomeBonusClaimed: boolean;
};

export async function getMe(): Promise<UserMeResponse> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const response = await apiClient.get('/api/users/me');
      return response.data?.data;
    } catch (err) {
      lastError = err;
      if (attempt < RETRY_DELAYS_MS.length && shouldRetryGetMe(err)) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
      } else {
        throw err;
      }
    }
  }
  throw lastError;
}

export async function updateMe(payload: { phone?: string; name?: string; email?: string }): Promise<UserMeResponse> {
  const response = await apiClient.patch('/api/users/me', payload);
  return response.data?.data;
}

/** CHANGE: update display name overrides (empty string or null = clear, use Telegram again). */
export async function updateMeName(payload: {
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
}): Promise<UserMeResponse> {
  const response = await apiClient.patch('/api/users/me/name', payload);
  return response.data?.data;
}

export async function claimWelcomeBonus(phone: string): Promise<ClaimWelcomeBonusResponse> {
  const response = await apiClient.post('/api/users/me/claim-welcome-bonus', { phone });
  return response.data?.data;
}

/** Версия политики конфиденциальности (152-ФЗ: фиксация при согласии). Менять при существенных изменениях текста. */
export const PRIVACY_POLICY_VERSION = '1.0';

export async function acceptPrivacyPolicy(payload?: {
  privacy_policy_version?: string;
}): Promise<{
  success: boolean;
  privacyPolicyAcceptedAt: string | null;
  privacyPolicyVersion?: string | null;
}> {
  const response = await apiClient.post('/api/users/me/accept-privacy-policy', {
    privacy_policy_version: payload?.privacy_policy_version ?? PRIVACY_POLICY_VERSION,
  });
  return response.data?.data;
}
